import JSZip from 'jszip'
import shpwrite from '@mapbox/shp-write'
import { kml } from '@tmcw/togeojson'
import { saveAs } from 'file-saver'

//TYPES
export type FormatoSaida = 'shp_zip' | 'shp';

export interface ItemFormato {
    id: number;
    nome: string;
    formato: FormatoSaida;
    descricao: string;
}

export interface ResumoGeometrias {
    total: number;
    poligonos: number;
    linhas: number;
    pontos: number;
}

export interface ItemFeicao {
    nome: string;
    tipo: string;
}

/**formatos de saída disponíveis (novos formatos devem ser adicionados aqui) */
export const opcoesFormato: ItemFormato[] = [
    { id: 1, nome: 'Shapefile zipado (.zip)', formato: 'shp_zip', descricao: 'arquivo .zip contendo .shp, .shx, .dbf e .prj' },
    { id: 2, nome: 'Shapefile (arquivos separados)', formato: 'shp', descricao: 'baixa os arquivos .shp, .shx, .dbf e .prj individualmente' },
];

/**Lê o arquivo KML e retorna o GeoJSON (FeatureCollection) já preparado para conversão */
export async function lerArquivoKml(file: File): Promise<any> {
    const texto = await lerComoTexto(file);

    const dom = new DOMParser().parseFromString(texto, 'text/xml');

    if (dom.getElementsByTagName('parsererror').length > 0) {
        throw new Error('O arquivo selecionado não é um KML válido!');
    }

    const geojson = kml(dom);

    if (!geojson?.features || geojson.features.length === 0) {
        throw new Error('Nenhuma geometria foi encontrada no KML!');
    }

    return prepararGeoJson(geojson);
}

/**Lê o conteúdo do arquivo como texto */
function lerComoTexto(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Falha ao ler o arquivo selecionado!'));
        reader.readAsText(file);
    });
}

/**Prepara o GeoJSON para o formato shapefile:
 * - separa GeometryCollection/Multi* em feições individuais
 * - remove altitude das coordenadas (shapefile 2D)
 * - normaliza as propriedades para o padrão DBF
 */
function prepararGeoJson(geojson: any) {
    let features: any[] = [];

    (geojson?.features ?? []).forEach((feature: any) => {
        features = features.concat(explodirGeometrias(feature));
    });

    features = features
        .filter(f => !!f?.geometry?.coordinates && f?.geometry?.coordinates?.length > 0)
        .map((feature, index) => ({
            type: 'Feature',
            geometry: {
                type: feature.geometry.type,
                coordinates: removerAltitude(feature.geometry.coordinates),
            },
            properties: normalizarPropriedades(feature?.properties, index),
        }));

    if (features.length === 0) {
        throw new Error('Nenhuma geometria válida foi encontrada no KML!');
    }

    return { type: 'FeatureCollection', features };
}

/**Converte MultiPolygon/MultiLineString/MultiPoint/GeometryCollection em feições simples */
function explodirGeometrias(feature: any): any[] {
    const geometry = feature?.geometry;

    if (!geometry) return [];

    if (geometry.type === 'GeometryCollection') {
        return (geometry.geometries ?? []).reduce((acc: any[], geo: any) => {
            return acc.concat(explodirGeometrias({ ...feature, geometry: geo }));
        }, []);
    }

    const tipo_simples = {
        'MultiPolygon': 'Polygon',
        'MultiLineString': 'LineString',
        'MultiPoint': 'Point',
    }?.[geometry.type];

    if (tipo_simples) {
        return (geometry.coordinates ?? []).map((coords: any) => ({
            ...feature,
            geometry: { type: tipo_simples, coordinates: coords },
        }));
    }

    return [feature];
}

/**Remove a altitude ([lng, lat, alt] → [lng, lat]) de qualquer nível de aninhamento */
function removerAltitude(coords: any): any {
    if (Array.isArray(coords?.[0])) {
        return coords.map((item: any) => removerAltitude(item));
    }
    return [coords?.[0], coords?.[1]];
}

/**Normaliza as propriedades para o padrão DBF (chaves com no máximo 10 caracteres, apenas valores primitivos) */
function normalizarPropriedades(properties: any, index: number) {
    let normalizadas: Record<string, string | number> = {};

    Object.keys(properties ?? {}).forEach((chave) => {
        const valor = properties?.[chave];
        if (valor === null || valor === undefined || typeof valor === 'object' || typeof valor === 'function') return;

        let nova_chave = chave
            .normalize('NFD')
            .split('')
            .filter(char => char.charCodeAt(0) < 768 || char.charCodeAt(0) > 879) //remove acentos (combining marks)
            .join('')
            .replace(/[^a-zA-Z0-9_]/g, '_')
            .slice(0, 10);

        if (nova_chave === '' || normalizadas[nova_chave] !== undefined) return;

        normalizadas[nova_chave] = typeof valor === 'number' ? valor : String(valor).slice(0, 254);
    });

    if (normalizadas?.name === undefined) {
        normalizadas = { name: `Feicao ${index + 1}`, ...normalizadas };
    }

    return normalizadas;
}

/**Monta o resumo das geometrias para exibir na tela */
export function resumoGeometrias(geojson: any): ResumoGeometrias {
    const features = geojson?.features ?? [];

    return {
        total: features.length,
        poligonos: features.filter((f: any) => f?.geometry?.type === 'Polygon').length,
        linhas: features.filter((f: any) => f?.geometry?.type === 'LineString').length,
        pontos: features.filter((f: any) => f?.geometry?.type === 'Point').length,
    };
}

/**Lista as feições (nome + tipo) para exibir na tabela de pré-visualização */
export function listarFeicoes(geojson: any): ItemFeicao[] {
    const legenda_tipo = {
        'Polygon': 'Polígono',
        'LineString': 'Linha',
        'Point': 'Ponto',
    };

    return (geojson?.features ?? []).map((feature: any, index: number) => ({
        nome: String(feature?.properties?.name ?? `Feição ${index + 1}`),
        tipo: legenda_tipo?.[feature?.geometry?.type] ?? feature?.geometry?.type,
    }));
}

/**Remove a extensão e caracteres inválidos do nome do arquivo */
export function obterNomeBase(nome_arquivo: string): string {
    const nome = (nome_arquivo ?? 'conversao').replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '').trim();
    return nome === '' ? 'conversao' : nome;
}

/**Converte o GeoJSON para shapefile e faz o download no formato escolhido */
export async function converterParaShapefile(geojson: any, nome_base: string, formato: FormatoSaida): Promise<void> {
    /**o .d.ts da lib declara a chave "line", porém em runtime a chave usada é "polyline" (zip.js: l.type.toLowerCase()), por isso o cast */
    const opcoes: any = {
        outputType: 'arraybuffer',
        compression: 'DEFLATE',
        types: {
            polygon: nome_base,
            polyline: `${nome_base}_linhas`,
            point: `${nome_base}_pontos`,
        },
    };

    const zip_buffer = await shpwrite.zip(geojson, opcoes);

    if (formato === 'shp_zip') {
        saveAs(new Blob([zip_buffer as ArrayBuffer], { type: 'application/zip' }), `${nome_base}.zip`);
        return;
    }

    /**formato 'shp': extrai o zip e baixa cada arquivo (.shp, .shx, .dbf, .prj) individualmente */
    const zip = await JSZip.loadAsync(zip_buffer as ArrayBuffer);

    const arquivos = Object.keys(zip.files).map(nome => zip.files[nome]).filter(item => !item.dir);

    for (const arquivo of arquivos) {
        const blob = await arquivo.async('blob');
        saveAs(blob, arquivo.name.split('/').pop());
        /**intervalo entre downloads para o navegador não bloquear os múltiplos arquivos */
        await new Promise(resolve => setTimeout(resolve, 350));
    }
}
